import { useEffect, useCallback, useState } from 'react';
import { Dimensions, Image as RNImage, Platform } from 'react-native';
import {
  useSharedValue,
  useAnimatedStyle,
  interpolate,
  Extrapolation,
} from 'react-native-reanimated';

interface UseProfileAnimationsProps {
  imageUrl?: string;
  tabsLayoutY?: number;
  headerHeight?: number;
}

export function useProfileAnimations({ imageUrl, tabsLayoutY = 0, headerHeight = 0 }: UseProfileAnimationsProps) {
  const scrollY = useSharedValue(0);
  const tabsThreshold = useSharedValue(0);
  
  const [imageHeight, setImageHeight] = useState(0);
  
  const screenWidth = Dimensions.get('window').width;
  const screenHeight = Dimensions.get('window').height;

  // Calculate image height based on photo dimensions
  useEffect(() => {
    let isMounted = true;
    let wasHeightSet = false;

    if (imageUrl) {
      RNImage.getSize(
        imageUrl,
        (width: number, height: number) => {
          if (!isMounted) return;
          wasHeightSet = true;
          const aspectRatio = height / width;
          const proportionalHeight = screenWidth * aspectRatio;
          const maxHeight = (screenHeight * 2) / 3;
          const minHeight = (screenWidth * 3) / 4;
          setImageHeight(Math.max(minHeight, Math.min(proportionalHeight, maxHeight)));
        },
        (error: any) => {
          if (!isMounted) return;
          wasHeightSet = true;
          console.warn('Failed to get image size:', error);
          setImageHeight(400); // Fallback to a default if it fails
        }
      );

      // Safety fallback if getSize takes too long
      const timer = setTimeout(() => {
        if (isMounted && !wasHeightSet) {
          setImageHeight(400);
        }
      }, 3000);

      return () => {
        isMounted = false;
        clearTimeout(timer);
      };
    } else if (imageUrl === '') {
      // If there's explicitly no photo, set height to 0
      setImageHeight(0);
    }
  }, [imageUrl, screenWidth, screenHeight]);

  // Update tabsThreshold when imageHeight or tabsLayoutY changes
  useEffect(() => {
    if (tabsLayoutY > 0 && headerHeight > 0) {
      // Account for the -mt-5 negative margin on the content container
      // Different platforms handle layout measurements differently
      const negativeMarginOffset = Platform.OS === 'web' ? 20 : 18;
      tabsThreshold.value = tabsLayoutY + imageHeight - headerHeight - negativeMarginOffset;
    }
  }, [imageHeight, tabsLayoutY, headerHeight, tabsThreshold]);

  // Function to update scroll Y (to be called from external scroll handler)
  const updateScrollY = useCallback((y: number) => {
    'worklet';
    scrollY.value = y;
  }, [scrollY]);

  // Animation for the background image
  const backgroundImageStyle = useAnimatedStyle(() => {
    const translateY = interpolate(
      scrollY.value,
      [0, imageHeight],
      [0, -imageHeight / 4],
      Extrapolation.CLAMP
    );

    // Scale effect when scrolling up (pulling down)
    const scale = interpolate(
      scrollY.value,
      [-200, 0],
      [2.0, 1],
      Extrapolation.CLAMP
    );

    // Additional translateY when pulling down to keep image aligned with content
    const pullDownTranslateY = interpolate(
      scrollY.value,
      [-200, 0],
      [imageHeight * 0.4, 0],
      Extrapolation.CLAMP
    );

    return {
      transform: [{ translateY: translateY + pullDownTranslateY }, { scale }],
    };
  });

  /**
   * Drives the opacity of a pre-rendered, statically-blurred copy of the header image
   * stacked on top of the sharp image. Animating opacity around a static blurred layer
   * is the cross-platform best practice — animating `blurRadius` / `filter: blur(Npx)`
   * per frame triggers shader/displaylist rebuilds and caused the Android ANR cluster
   * (GROKIO-APP-MC, MD, N0, MP, VF) on 6.0.194.
   *
   * The opacity is eased (quadratic ease-out) rather than linear: a linear opacity
   * crossfade between sharp and a 20px-blurred layer doesn't feel like a linear blur
   * ramp — the eye reads the midpoint of a crossfade as "still mostly sharp" rather
   * than "half blurred". Easing-out makes the perceived blur kick in at the same
   * scroll position the previous animated `blurRadius` did.
   */
  const blurredImageOpacityStyle = useAnimatedStyle(() => {
    const t = interpolate(
      scrollY.value,
      [imageHeight * 0.5, imageHeight],
      [0, 1],
      Extrapolation.CLAMP
    );
    const eased = 1 - (1 - t) * (1 - t);

    return { opacity: eased };
  });

  // Animation for the darkening overlay
  const darkenOverlayStyle = useAnimatedStyle(() => {
    const opacity = interpolate(
      scrollY.value,
      [imageHeight * 0.5, imageHeight],
      [0, 0.2],
      Extrapolation.CLAMP
    );

    return {
      opacity,
    };
  });

  // Animation for the fixed header - simple fade in effect
  const fixedHeaderStyle = useAnimatedStyle(() => {
    const opacity = interpolate(
      scrollY.value,
      [imageHeight * 0.75, imageHeight],
      [0, 1],
      Extrapolation.CLAMP
    );

    return {
      opacity,
    };
  });

  // Animation for the content background that covers area below the photo
  const contentBackgroundStyle = useAnimatedStyle(() => {
    // Start the background at the bottom of the image and move it up 1:1 with scroll
    const basePosition = imageHeight;
    
    // Move up at the same speed as scroll (1:1 ratio for direct sync)
    const scrollOffset = -scrollY.value;

    // Additional offset when pulling down (only for pull-to-refresh)
    const pullDownOffset = interpolate(
      scrollY.value,
      [-200, 0],
      [imageHeight * 0.4, 0],
      Extrapolation.CLAMP
    );

    // Calculate final position but never let it go above the header
    const translateY = Math.max(headerHeight, basePosition + scrollOffset + pullDownOffset);

    return {
      transform: [{ translateY }],
    };
  });

  return {
    // Values
    imageHeight,
    tabsThreshold,
    
    // Handlers
    updateScrollY,
    
    // Animated styles
    backgroundImageStyle,
    blurredImageOpacityStyle,
    darkenOverlayStyle,
    fixedHeaderStyle,
    contentBackgroundStyle,
  };
}